// quote-preview-v4.component.ts — VERSION COMPLÈTE PATCHÉE
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, AfterViewInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { BridgeApiService } from '../services/bridge-api.service';
import { CHAIN_MAP, getNetworkName, getNetworkIcon } from '../utils/chains';

// ⬇️ IMPORT SERVICE (ajuste le chemin si besoin)
import { BalanceValidatorService } from '../services/balance-validator.service'; // <

// Shape léger pour une route/quote (remplace par tes vraies interfaces)
export interface BridgeQuoteLike {
  provider?: string;
  providerLogo?: string;

  // montants (raw/pretty)
  estimatedBuyAmount?: string | number; // reçu (token)
  estimatedBuyFiat?: number;            // ≈ en devise sélectionnée
  sellSymbol?: string;
  buySymbol?: string;

  // pricing
  rate?: number;                        // 1 SELL = rate BUY
  priceImpactPct?: number | null;
  maxSlippagePct?: number | null;

  // temps
  estimatedDurationSeconds?: number | null;

  // fees (idéalement champs LiFi normalisés)
  lifiGasAmountWei?: string | number | bigint;
  lifiGasPriceWei?: string | number | bigint;
  lifiEstimatedGas?: string | number | bigint;
  gasTokenSymbol?: string;              // POL, ETH, etc.
  bridgeFeeFiat?: number | null;        // si fourni par le backend

  // exécution (enable/disable validate pour alternatives)
  to?: string | null;
  data?: string | null;

  bridgeName?: string;
  BridgeName?: string;
  bridgeIcon?: string;
  BridgeIcon?: string;
  providerName?: string;
  providerKey?: string;
  providerLogoUrl?: string;
  logoURI?: string;

  toolDetails?: { name?: string; logoURI?: string } | null;
  steps?: Array<{ toolDetails?: { name?: string; logoURI?: string } | null }> | null;
  route?: { toolDetails?: { name?: string; logoURI?: string } | null } | null;
  singleRoutes?: any[] | null;   // parfois le nom/logo ne vit que dans la route sélectionnée
  SingleRoutes?: any[] | null;   // variante de casse
}
type CtaAction = 'connect' | 'approve_then_validate' | 'validate' | null;
type CtaState = { label: string; disabled: boolean; action: CtaAction; reasons?: string[]; deficitFiat?: string | null; };
@Component({
  selector: 'app-quote-preview-v4',
  standalone: true,
  imports: [CommonModule, TranslateModule], // ✅ ajoute TranslateModule
  templateUrl: './quote-preview-v4.component.html',
  styleUrls: ['./quote-preview-v4.component.scss'] // Ajout d'un SCSS dédié pour un design pro, inspiré Uniswap
})
export class QuotePreviewComponentV4 implements OnChanges, AfterViewInit {
  @Input() quote: any;
  @Input() signature: string = '';
  @Input() fiatCode = 'USD';
  @Input() fiatRates: Record<string, number> = { USD: 1 };
  @Input() swapStatus: 'idle' | 'approve' | 'pending' | 'success' | 'error' | 'cancelled' = 'idle';
  @Input() swapHash: string | null = null;
  @Input() swapError: string | null = null;
  @Input() validateAlternative?: (route: any) => { disabled: boolean; reasons: string[] };
  @Output() executeRequest = new EventEmitter<void>();
  @Output() selectAlternative = new EventEmitter<any>();
  @Output() copyToClipboard = new EventEmitter<string>();
  @Input() swapDisabled = false;
  @Input() nativeTokenUsd = 0;
  @Input() gasPriceWei: bigint = 0n;
  @Input() fullGasWei: bigint = 0n;
  // --- CTA: données & events ---
  @Input() takerAddress?: string | null;                 // pour savoir si le wallet est connecté
  @Input() needsApproval?: boolean = false;              // main: allowance insuffisante ?
  @Input() needsApprovalForAlt?: (r: any) => boolean;    // alt: predicate par route

  @Output() requestConnect = new EventEmitter<void>();   // demander au parent d'ouvrir le connect modal
  @Output() approveThenValidate = new EventEmitter<any>(); // enchaîner approve -> validate (main: null, alt: route)

  @Output() close = new EventEmitter<void>();
  @Input() bypassDebug: boolean = false;
  // ⬇️ ajoutés pour matcher le HTML
  @Output() share = new EventEmitter<void>();
  @Output() executeAlt = new EventEmitter<any>();
  @Input() bridgeReasons: string[] = [];
  @Input() nativeBalanceWei: bigint = 0n;
  // en haut avec les autres @angular/core déjà présents
  // QuotePreviewV4 (TS)
  @Input() validatingKey: string | null = null;

  private getQuoteKey(r: any): string {
    const raw =
      r?.providerKey ?? r?.ProviderKey ??
      r?.toolKey ?? r?.tool ??
      r?.source ?? r?.Source ??
      r?.providerName ?? r?.name ??
      r?.routeId ?? r?.id ?? '';
    let key = String(raw).trim();
    if (!key) {
      const chain = r?.chainId ?? r?.fromChainId ?? r?.chain ?? 'na';
      const sell = (r?.sellToken?.symbol ?? r?.fromToken?.symbol ?? r?.sellTokenSymbol ?? 'SELL').toUpperCase();
      const buy = (r?.buyToken?.symbol ?? r?.toToken?.symbol ?? r?.buyTokenSymbol ?? 'BUY').toUpperCase();
      key = `main:${chain}:${sell}->${buy}`;
    }
    return key.toLowerCase();
  }

  isMainActive(): boolean {
    if (!this.validatingKey) return false;
    try { return this.getQuoteKey(this.quote) === this.validatingKey; } catch { return false; }
  }

  isAltActive(r: any): boolean {
    if (!this.validatingKey) return false;
    try { return this.getQuoteKey(r) === this.validatingKey; } catch { return false; }
  }


  // ── Sticky footer: refs & flags
  @ViewChild('scrollArea') scrollArea?: ElementRef<HTMLDivElement>;
  isScrollable = false;
  showStickyFooter = false;

  ngAfterViewInit(): void {
    // Double-sécurité après le 1er paint : affiche tout de suite si nécessaire
    this.scheduleAutoWhy();
    this.recomputeStickyFooter();
    this.scheduleAutoWhy();         // calcule et lance les timers si conditions ok
    this.kickWhyTimersOnce();       // si tu avais forcé showWhy...=true, on lance le timer manquant
    this.recomputeStickyFooter();
  }

  // États UI
  showDetails = false;
  showAlternatives = false;
  private autoHideTimeout?: any;
  private pendingTimeout?: any;
  private tooltipTimeout?: any;
  showTooltip = false;
  // index de l’alternative en cours (null si rien)
  lastAltStatusIndex: number | null = null;

  // --- ÉTAT TOOLTIP (clic) ---
  openedTooltip: string | null = null;

  private helpSlugs: Record<'slippage' | 'price_impact' | 'network_fees', string> = {
    slippage: 'slippage-crypto-explication',
    price_impact: 'price-impact-crypto-explication',
    network_fees: 'frais-reseau-crypto-explication'
  };



  // ✅ Version tolérante + logs détaillés
  toggleTooltip(
    topic: 'slippage' | 'price_impact' | 'network_fees',
    a?: string | MouseEvent,
    b?: MouseEvent
  ): void {
    let key: string | undefined;
    let ev: MouseEvent | undefined;

    if (a && typeof (a as any).stopPropagation === 'function') {
      ev = a as MouseEvent;     // appel (topic, $event)
    } else {
      key = a as string | undefined; // appel (topic, 'alt-'+i, $event)
      ev = b;
    }

    if (ev) ev.stopPropagation();

    const id = key ? `${topic}:${key}` : topic;
    this.openedTooltip = (this.openedTooltip === id) ? null : id;
  }




  closeTooltip(): void {
    this.openedTooltip = null;
  }

  getHelpUrl(topic: 'slippage' | 'price_impact' | 'network_fees'): string {
    const lang = (this.translate?.currentLang || 'en').toLowerCase();
    return `/${lang}/blog/${this.helpSlugs[topic]}`;
  }

  // Fermer au clic à l’extérieur
  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!this.openedTooltip) return;
    // Si le clic est hors du composant → fermer
    const target = e.target as Node;
    if (!this.el?.nativeElement?.contains?.(target)) {
      this.closeTooltip();
    }
  }

  // Fermer avec Échap
  @HostListener('document:keydown.escape')
  onEsc(): void {
    this.closeTooltip();
  }


  private _fallbackFullGasWei: bigint = 0n;
  private selectedAltKey: string | null = null;
  private currentQuoteKey: string | null = null;
  // Slippage par défaut
  // 0.5 % de slippage
  private readonly defaultSlippagePct = 0.5;

  // === WHY DISABLED (main + alts) ==========================
  showWhyMain = false;
  showWhyAlt: { [index: number]: boolean } = {};
  private whyTimers = new Map<string, any>(); // gère les timeouts par clé

  private lastWhyKeyMain: string | null = null;
  private lastWhyKeysAlt: Record<number, string | null> = {};
  expandedAltIndex: number | null = null;
  /** Construit une clé "gating" : connex + disabled + reasons */
  /** Construit une clé "gating" : connexion + état CTA + raisons */
  private buildWhyKeyMain(): string {
    const connected = this.isWalletConnected();
    const cta = this.getMainCtaState();
    const disabled = !!cta.disabled;
    const reasonsArr = cta.reasons ?? [];
    const reasonsJoined = reasonsArr.join('|');
    const key = `${connected}-${disabled}-${reasonsJoined}`;

    this.log('buildWhyKeyMain()', {
      connected,
      disabled,
      reasonsArr,
      key
    });

    return key;
  }

  private buildWhyKeyAlt(i: number, r: any): string {
    const connected = this.isWalletConnected();
    const disabled = !!(this.isAltValidateDisabled?.(r));
    const reasons = (this.getAltReasons?.(r) || []).join('|');
    return `${i}-${connected}-${disabled}-${reasons}`;
  }

  /** Lance un timer 4s pour la clé (main|alt-i), puis masque */
  private startWhyTimer(key: string, ms = 4000) {
    const prev = this.whyTimers.get(key);
    if (prev) clearTimeout(prev);
    // On force l'état visible AVANT de lancer le timer (au cas où)
    if (key === 'main') this.showWhyMain = true;
    else {
      const m = key.match(/^alt-(\d+)$/);
      if (m) this.showWhyAlt[+m[1]] = true;
    }
    const id = setTimeout(() => {
      if (key === 'main') this.showWhyMain = false;
      else {
        const m = key.match(/^alt-(\d+)$/);
        if (m) this.showWhyAlt[+m[1]] = false;
      }
      this.whyTimers.delete(key);
      this.recomputeStickyFooter();
    }, ms);
    this.whyTimers.set(key, id);
  }
  /** Démarre un timer pour tout WHY déjà visible (main + alts) */
  private kickWhyTimersOnce() {
    if (this.showWhyMain && !this.whyTimers.has('main')) {
      this.startWhyTimer('main', 4000);
    }
    Object.keys(this.showWhyAlt).forEach(k => {
      const i = Number(k);
      if (this.showWhyAlt[i] && !this.whyTimers.has(`alt-${i}`)) {
        this.startWhyTimer(`alt-${i}`, 4000);
      }
    });
  }

  /** Affiche/re-boucle le WHY main pendant 4s */
  /** Révèle le "pourquoi désactivé" (main) sur action utilisateur */
  revealMainWhy(): void {
    const connected = this.isWalletConnected();
    const cta = this.getMainCtaState();
    const hasReasons = (cta.reasons?.length ?? 0) > 0;

    this.log('revealMainWhy(): pre-check', {
      connected,
      disabled: !!cta.disabled,
      reasons: cta.reasons,
      hasReasons
    });

    if (!connected) {
      this.log('revealMainWhy(): aborted — wallet NOT connected');
      return;
    }
    if (!(cta.disabled && hasReasons)) {
      this.log('revealMainWhy(): aborted — not (disabled && hasReasons)');
      return;
    }

    this.showWhyMain = true;
    this.log('revealMainWhy(): SHOW WHY (4s timer)');
    this.startWhyTimer('main', 4000);
    this.lastWhyKeyMain = this.buildWhyKeyMain();
    this.recomputeStickyFooter();
  }


  /** Affiche/re-boucle le WHY d’une alternative i pendant 4s */
  revealAltWhy(i: number, r: any) {
    if (!this.isWalletConnected()) return;
    const reasons = this.getAltReasons?.(r) ?? [];
    if (!(this.isAltValidateDisabled?.(r) && reasons.length > 0)) return;
    this.showWhyAlt[i] = true;
    this.startWhyTimer(`alt-${i}`, 4000);
    this.lastWhyKeysAlt[i] = this.buildWhyKeyAlt(i, r);

    this.recomputeStickyFooter();
  }

  /** Au chargement/changement: auto-affiche 4s si nécessaire (main + alts) */
  /** Affiche auto le "Why disabled" 4s (main + alts) sans réarmer le timer en boucle */
  /** Gère l’auto-affichage 4s du "pourquoi désactivé" (main) */
  private scheduleAutoWhy(): void {
    const connected = this.isWalletConnected?.() ?? false;
    const cta = this.getMainCtaState();
    const mainReasons = cta.reasons ?? [];
    const canShowMain = connected && !!cta.disabled && mainReasons.length > 0;

    // Clé de gating: état "significant" (connex + disabled + reasons)
    const keyMain = `main:${connected}-${!!cta.disabled}-${mainReasons.join('|')}`;

    this.log('scheduleAutoWhy(): snapshot', {
      connected,
      disabled: !!cta.disabled,
      reasons: mainReasons,
      canShowMain,
      keyMain,
      lastWhyKeyMain: this.lastWhyKeyMain,
      showWhyMain: this.showWhyMain
    });

    if (!canShowMain) {
      if (this.showWhyMain || this.lastWhyKeyMain !== null) {
        this.log('scheduleAutoWhy(): HIDE WHY (conditions not met)');
      }
      this.showWhyMain = false;
      this.lastWhyKeyMain = null;
      return;
    }

    if (this.lastWhyKeyMain !== keyMain) {
      this.log('scheduleAutoWhy(): SHOW WHY (state changed → start 4s timer)');
      this.showWhyMain = true;
      this.startWhyTimer('main', 4000);
      this.lastWhyKeyMain = keyMain;
    } else {
      this.log('scheduleAutoWhy(): keep WHY (no significant change)');
    }
  }



  getMinReceivedAmount(): string {
    // mêmes fallbacks que estimatedReceived
    const dec = Number(
      this.quote?.buyTokenDecimals ??
      this.quote?.BuyTokenDecimals ??
      this.quote?.toToken?.decimals ?? 18
    );

    // 1) Priorité au formaté (comme estimatedReceived)
    let amount = Number(this.quote?.formattedBuyAmount ?? 0);

    // 2) Sinon, normaliser le raw avec toutes les variantes de casse
    if (!(amount > 0)) {
      let raw = this.quote?.toAmountRaw ??
        this.quote?.ToAmountRaw ??
        this.quote?.toAmount ??
        this.quote?.ToAmount ??
        this.quote?.toAmountMin ?? 0;

      let rawNum = 0;
      if (typeof raw === 'bigint') rawNum = Number(raw);
      else if (typeof raw === 'string' && raw.startsWith('0x')) {
        try { rawNum = Number(BigInt(raw)); } catch { rawNum = 0; }
      } else {
        rawNum = Number(raw || 0);
      }
      amount = rawNum / Math.pow(10, dec);
    }

    // 3) Appliquer -0.5 %
    const min = amount * (1 - this.defaultSlippagePct / 100);
    return (min >= 1 ? min.toFixed(6) : min.toFixed(8)).replace(/\.?0+$/, '');
  }



  // Retourne une traduction lisible ou un fallback propre
  getReasonLabel(code: string, route?: any): string {
    const key = `BRIDGE_REASON_${code}`; // ex: BRIDGE_REASON_INSUFFICIENT_NATIVE_GAS
    const params = this.getReasonParams(code, route);
    const t = this.translate.instant(key, params);
    return t && t !== key ? t : code.replace(/_/g, ' ');
  }

  // Paramètres d’interpolation selon la raison
  private getReasonParams(code: string, route?: any): any {
    if (code === 'INSUFFICIENT_NATIVE_GAS') {
      const cid = Number(
        route?.fromChainId ?? route?.chainId ?? this.quote?.fromChainId ?? this.quote?.chainId
      );
      return { symbol: this.canonicalNativeSymbol?.(cid) ?? 'ETH' };
    }
    if (code === 'INSUFFICIENT_SELL_TOKEN') {
      const token =
        route?.sellTokenSymbol ?? this.sellTokenSymbol ?? this.quote?.sellTokenSymbol ?? '';
      return { token };
    }
    return {};
  }
  // 🔹 Helpers "label ou symbole" pour éviter dépendances
  private labelOf(sym?: string): string {
    const s = (sym || '').toUpperCase();
    const getLabel = (this as any).getTokenLabel;
    return typeof getLabel === 'function' ? getLabel(s) : s;
  }

  // 🔹 Montant vendu formaté (prend formattedSellAmount si dispo, sinon fromAmount+decimals)
  private getFormattedSellAmount(): string {
    const dec =
      Number(this.quote?.sellTokenDecimals ?? this.quote?.fromToken?.decimals ?? 18) || 18;

    let v = Number(this.quote?.formattedSellAmount);
    if (!(v > 0)) {
      let raw = this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.FromAmount ?? 0;
      if (typeof raw === 'string' && raw.startsWith('0x')) {
        try { raw = BigInt(raw); } catch { raw = 0; }
      }
      const rawNum = typeof raw === 'bigint' ? Number(raw) : Number(raw || 0);
      v = rawNum / Math.pow(10, dec);
    }
    if (!isFinite(v) || v <= 0) v = 0;

    // Format court, propre, tabular-nums côté HTML
    const s = v >= 1 ? v.toFixed(6) : v.toFixed(6);
    return s.replace(/\.?0+$/, '');
  }

  // 🔹 Ligne Résumé: "1 USDC → ETH"
  getSummaryLine(): string {
    const sell = this.getFormattedSellAmount?.() ?? '—';
    const sSym = this.sellTokenSymbol || '—';
    const bSym = this.buyTokenSymbol || '—';
    return `${sell} ${sSym} → ${bSym}`;
  }


  getMinReceivedFiat(): string {
    const usd = Number(this.quote?.buyTokenUsdPrice ?? 0); // prix USD du token reçu
    if (!usd) return '';
    const min = Number(this.getMinReceivedAmount());
    const v = min * usd;
    return this.formatFiat(v); // ✅ un seul argument
  }

  gasLevel(r: any): 'ok' | 'mid' | 'high' {
    // Si tu as déjà getGasLevel(r), on le respecte
    if (typeof (this as any).getGasLevel === 'function') {
      const v = (this as any).getGasLevel(r);
      return (v === 'ok' || v === 'mid' || v === 'high') ? v : 'mid';
    }
    // Sinon: ok = validable, mid = non validable
    return this.isAltValidateDisabled(r) ? 'mid' : 'ok';
  }

  getRouteMinReceivedAmount(r: any): string {
    const decimals = Number(r?.toTokenDecimals ?? r?.toToken?.decimals ?? 18);
    const raw = Number(r?.toAmountRaw ?? r?.estimate?.toAmount ?? 0);
    const quoted = raw / Math.pow(10, decimals);
    const min = quoted * (1 - this.defaultSlippagePct / 100);
    return (min >= 1 ? min.toFixed(6) : min.toFixed(8)).replace(/\.?0+$/, '');
  }

  getRouteMinReceivedFiat(r: any): string {
    const usd = Number(r?.buyTokenUsdPrice ?? r?.estimate?.toToken?.priceUSD ?? 0);
    if (!usd) return '';
    const min = Number(this.getRouteMinReceivedAmount(r));
    const v = min * usd;
    return this.formatFiat(v); // ✅ un seul argument
  }

  // Ancien set (on le garde), + support par index pour le HTML actuel
  expandedAlts: Set<string> = new Set<string>();
  expandedAlt: { [index: number]: boolean } = {};

  readonly CHAIN_MAP = CHAIN_MAP;
  keyOf = (_: number, r: any) =>
    (this.getProviderKey?.(r)) ||
    r?.id ||
    `${r?.providerName ?? r?.toolKey ?? 'route'}-${_}`;

  constructor(private translate: TranslateService, private bridgeApi: BridgeApiService, private el: ElementRef,) {
    // brancher le bouton Share si le template reste (click)="share.emit()"
    this.share.subscribe(() => this.onShareClick());
  }
  private weiToFiat(wei: bigint): string | null {
    const usdPerNative =
      Number(this.quote?.gasTokenPriceUsd ?? this.quote?.GasTokenPriceUsd ?? this.nativeTokenUsd ?? 0);
    if (!usdPerNative) return null;
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const native = Number(wei) / 1e18;
    const fiat = native * usdPerNative * rate;
    const locale = this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US';
    return `≈ ${fiat.toLocaleString(locale, {
      style: 'currency',
      currency: this.fiatCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }
  isWalletConnected(): boolean {
    return !!this.takerAddress && String(this.takerAddress) !== '';
  }
  private isBusy(): boolean {
    return this.swapStatus === 'approve' || this.swapStatus === 'pending';
  }
  private hasInvalidAmount(): boolean {
    return Array.isArray(this.bridgeReasons) && this.bridgeReasons.includes('INVALID_AMOUNT');
  }

  // Normaliser une valeur déjà en wei → bigint (0 si invalide)
  private parseWei(v: any): bigint {
    try {
      if (typeof v === 'bigint') return v;
      if (typeof v === 'number') return BigInt(Math.trunc(v));
      if (typeof v === 'string') return v.startsWith('0x') ? BigInt(v) : BigInt(v || '0');
    } catch { }
    return 0n;
  }

  /** MM-like pour le gating: EXPECTED-first → mmNetwork → mmMax */
  private pickMmExpectedWei(r: any): bigint {
    return this.parseWei(
      r?.mmExpectedNetworkFeeWei ?? r?.mmNetworkFeeWei ?? r?.mmMaxNetworkFeeWei ?? 0
    );
  }

  /** État du CTA principal — version instrumentée */
  /** État du CTA principal — version stable avec fallbacks de raisons */
  // ✅ MAIN CTA — i18n only
  getMainCtaState(): CtaState {
    // 0) Savoir si la MAIN est bien la route active (clé = validatingKey)
    const mainActive = typeof this.isMainActive === 'function' ? this.isMainActive() : true;

    // 1) Wallet
    if (!this.isWalletConnected()) {
      return { label: this.translate.instant('CONNECT_WALLET'), disabled: false, action: 'connect' };
    }

    // 2) Busy (approve / pending) — n'afficher le libellé "…" que si la MAIN est active
    if (this.swapStatus === 'approve') {
      if (mainActive) {
        return { label: this.translate.instant('APPROVING_ELLIPSIS'), disabled: true, action: null };
      }
      // ALT en cours → bouton main désactivé mais libellé normal
      return { label: this.translate.instant('VALIDATE'), disabled: true, action: null, reasons: ['ALTERNATIVE_IN_PROGRESS'] };
    }
    if (this.swapStatus === 'pending') {
      if (mainActive) {
        return { label: this.translate.instant('VALIDATING_ELLIPSIS'), disabled: true, action: null };
      }
      return { label: this.translate.instant('VALIDATE'), disabled: true, action: null, reasons: ['ALTERNATIVE_IN_PROGRESS'] };
    }


    // 3) Mode bypass debug (PATCH)
    // ⚠️ IMPORTANT: bypassDebug doit UNIQUEMENT bypass le contrôle "gas insuffisant".
    // Il ne doit JAMAIS bypass les checks métier (montant invalide, token insuffisant, allowance, chain mismatch, etc.)
    const bypassGasOnly = !!this.bypassDebug;

    // 4) Montant invalide
    if (this.hasInvalidAmount()) {
      const reasons = [...(this.bridgeReasons ?? [])];
      if (reasons.length === 0) reasons.push('INVALID_AMOUNT');
      return { label: this.translate.instant('VALIDATE'), disabled: true, action: null, reasons };
    }

    // 5) Gas insuffisant
    const deficit = this.getMainGasDeficitFiat?.() ?? null;
    if (deficit && !bypassGasOnly) {
      const reasons = [...(this.bridgeReasons ?? [])];
      if (reasons.length === 0) reasons.push('INSUFFICIENT_NATIVE_GAS');
      return { label: this.translate.instant('VALIDATE'), disabled: true, action: null, reasons, deficitFiat: deficit };
    }

    // 6) Allowance requise
    if (this.needsApproval) {
      return { label: this.translate.instant('APPROVE_AND_VALIDATE'), disabled: false, action: 'approve_then_validate' };
    }

    // 7) Cas final
    const disabled = !!this.swapDisabled;
    const reasons = [...(this.bridgeReasons ?? [])];
    if (disabled && reasons.length === 0) reasons.push('QUOTE_NOT_READY');
    return { label: this.translate.instant('VALIDATE'), disabled, action: disabled ? null : 'validate', reasons };
  }





  getAltCtaState(r: any): CtaState {
    // 1) Wallet
    if (!this.isWalletConnected()) {
      return { label: this.translate.instant('CONNECT_WALLET'), disabled: false, action: 'connect' };
    }

    // 2) Busy — bloquer TOUTES les alternatives quand un run est en cours
    if (this.swapStatus === 'approve' || this.swapStatus === 'pending') {
      return {
        label: this.translate.instant(
          this.swapStatus === 'approve'
            ? 'APPROVING_ELLIPSIS'
            : 'VALIDATING_ELLIPSIS'
        ),
        disabled: true,
        action: null,
        // optionnel : raison technique si un jour tu veux afficher un WHY
        // reasons: ['RUN_IN_PROGRESS']
      };
    }

    // 3) Montant invalide / blocages globaux
    const reasons = this.getAltReasons?.(r) ?? [];
    if (this.hasInvalidAmount()) {
      return { label: this.translate.instant('VALIDATE'), disabled: true, action: null, reasons: ['INVALID_AMOUNT'] };
    }
    const bypassGasOnly = !!this.bypassDebug;
    // 4) Gas insuffisant (par route) → montant manquant
    const altDisabled = this.isAltValidateDisabled?.(r) ?? false;
    const altDeficit = this.getAltGasDeficitFiat?.(r) ?? null;
    if (altDisabled && altDeficit && !bypassGasOnly) {
      return { label: this.translate.instant('VALIDATE'), disabled: true, action: null, reasons, deficitFiat: altDeficit };
    }

    // 5) Allowance requise pour CETTE route ?
    const needApproveAlt = this.needsApprovalForAlt?.(r) ?? false;
    if (needApproveAlt && !altDisabled) {
      return { label: this.translate.instant('APPROVE_AND_VALIDATE'), disabled: false, action: 'approve_then_validate' };
    }

    // 6) OK
    return {
      label: this.translate.instant('VALIDATE'),
      disabled: altDisabled,
      action: altDisabled ? null : 'validate',
      reasons
    };
  }

  /** Log helper (namespacé) */
  private log(_message: string, _extra?: any): void { /* noop: logs désactivés en prod */ }

  onMainCtaClick(): void {
    const cta = this.getMainCtaState();
    this.log('onMainCtaClick()', {
      disabled: !!cta.disabled,
      reasons: cta.reasons,
      showWhyMain: this.showWhyMain
    });

    // CTA désactivé → montrer le WHY
    if (cta.disabled || !cta.action) {
      this.revealMainWhy();
      return;
    }

    if (cta.action === 'connect') {
      this.requestConnect.emit();
      return;
    }

    if (cta.action === 'approve_then_validate' || cta.action === 'validate') {
      // UI: statut immédiat + reset messages
      this.swapStatus = 'pending';
      this.swapError = null;
      this.swapHash = null;

      // Par sécurité, on nettoie un éventuel scope d'alternative
      if ((this as any).lastAltStatusIndex !== undefined) {
        (this as any).lastAltStatusIndex = null;
      }

      // Laisse l'état "pending" se peindre puis lance le flux réel
      setTimeout(() => {
        if (cta.action === 'approve_then_validate') {
          this.approveThenValidate.emit(null); // main: pas de route à passer
        } else {
          this.executeRequest.emit();          // existant chez toi
        }
      }, 0);

      return;
    }
  }


  onAltCtaClick(r: any, i: number): void {
    const cta = this.getAltCtaState(r);

    // CTA désactivé → montrer le WHY 4s (alt i)
    if (cta.disabled || !cta.action) {
      this.revealAltWhy(i, r);
      return;
    }

    if (cta.action === 'connect') {
      this.requestConnect.emit();
      return;
    }

    // ✅ UI: basculer immédiatement en "pending" pour CETTE alternative
    this.lastAltStatusIndex = i;
    this.swapStatus = 'pending';
    this.swapError = null;
    this.swapHash = null;

    // Lancer le flux réel après paint
    setTimeout(() => {
      if (cta.action === 'approve_then_validate') {
        this.approveThenValidate.emit(r);   // approve -> validate pour l’alternative
      } else {
        this.selectAlternative.emit(r);      // validate direct (ta logique existante)
      }
    }, 0);
  }

  getMainCtaLabel(): string { return this.getMainCtaState().label; }
  isMainCtaDisabled(): boolean { return this.getMainCtaState().disabled; }

  getAltCtaLabel(r: any): string { return this.getAltCtaState(r).label; }
  isAltCtaDisabled(r: any): boolean { return this.getAltCtaState(r).disabled; }

  private isSellingNative(): boolean {
    const chainId = Number(this.quote?.fromChainId ?? this.quote?.chainId);
    const nat = this.canonicalNativeSymbol(chainId);
    return (this.sellTokenSymbol || '').toUpperCase() === nat.toUpperCase();
  }

  private getSellAmountWei(): bigint {
    const dec = Number(this.quote?.sellTokenDecimals ?? this.quote?.fromToken?.decimals ?? 18) || 18;
    let amt = Number(this.quote?.formattedSellAmount ?? 0);
    if (!(amt > 0)) {
      let raw = this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.FromAmount ?? 0;
      if (typeof raw === 'string' && String(raw).startsWith('0x')) {
        try { raw = BigInt(raw); } catch { raw = 0; }
      }
      const rawNum = typeof raw === 'bigint' ? Number(raw) : Number(raw || 0);
      amt = rawNum / Math.pow(10, dec);
    }
    return BigInt(Math.round(amt * 1e18));
  }

  /** Déficit fiat pour la QUOTE principale en cas d'INSUFFICIENT_NATIVE_GAS */
  getMainGasDeficitFiat(): string | null {
    const totalWei =
      (this.fullGasWei && this.fullGasWei > 0n) ? this.fullGasWei :
        (this._fallbackFullGasWei && this._fallbackFullGasWei > 0n) ? this._fallbackFullGasWei : 0n;
    if (totalWei <= 0n) return null;

    let needWei = totalWei;
    if (this.isSellingNative()) {
      needWei = this.getSellAmountWei();
    }
    if (this.nativeBalanceWei >= needWei) return null;
    const deficit = needWei - this.nativeBalanceWei;
    return this.weiToFiat(deficit);
  }

  /** Déficit fiat pour une ALTERNATIVE en cas d'INSUFFICIENT_NATIVE_GAS */
  getAltGasDeficitFiat(r: any): string | null {
    const toNum = (v: any): number => {
      if (v == null || v === '') return NaN;
      if (typeof v === 'bigint') return Number(v);
      const s = String(v);
      if (s.startsWith('0x')) { try { return Number(BigInt(s)); } catch { return NaN; } }
      const n = Number(s); return isFinite(n) ? n : NaN;
    };
    const toWei = (v: any): bigint => {
      try {
        if (typeof v === 'bigint') return v;
        if (typeof v === 'number') return BigInt(Math.trunc(v));
        if (typeof v === 'string') return v ? BigInt(v) : 0n;
      } catch { }
      return 0n;
    };

    // ✅ PRIORITÉ GATING: MM-expected → mmNetwork → mmMax
    let feeWei = this.pickMmExpectedWei(r);
    // 🔙 Fallback affichage si pas de MM: LiFi (amount) → LiFi (price*gas) → génériques
    if (feeWei <= 0n) {
      feeWei =
        toWei(r?.lifiGasAmountWei ?? r?.LifiGasAmountWei) ||
        (() => {
          const p = toNum(r?.lifiGasPriceWei ?? r?.LifiGasPriceWei);
          const g = toNum(r?.lifiEstimatedGas ?? r?.LifiEstimatedGas);
          return (p && g) ? BigInt(Math.round(p * g)) : 0n;
        })() ||
        toWei(r?.gasAmountWei ?? r?.GasAmountWei ?? r?.totalNetworkFee ?? r?.TotalNetworkFee) ||
        toWei(r?.gas?.fullWei);
    }

    if (feeWei <= 0n) return null;
    let needWei = feeWei;
    if (this.isSellingNative()) {
      needWei = this.getSellAmountWei();
    }
    if (this.nativeBalanceWei >= needWei) return null;
    const deficit = needWei - this.nativeBalanceWei;
    return this.weiToFiat(deficit);
  }
  // Alias showMainDetails <-> showDetails pour éviter les NG1
  get showMainDetails(): boolean { return this.showDetails; }
  set showMainDetails(v: boolean) { this.showDetails = v; setTimeout(() => this.recomputeStickyFooter(), 0); }

  private norm(s: any): string {
    return String(s ?? '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim();
  }
  private getProviderKey(x: any): string {
    return this.norm(
      x?.providerKey ?? x?.ProviderKey ?? x?.toolKey ?? x?.ToolKey ?? x?.tool ?? x?.Tool ?? x?.source ?? x?.Source ?? x?.executorKey ?? x?.ExecutorKey ?? x?.providerName ?? x?.ProviderName ?? x?.name
    );
  }
  private toNum(v: any): number {
    return typeof v === 'bigint' ? Number(v) : Number(v ?? 0);
  }
  isAltValidateDisabled(r: any): boolean {
    const res = this.validateAlternative?.(r);
    return !!res?.disabled; // ✅ aucune dépendance à swapDisabled ni à la présence de to/data
  }
  // ⬇️ nouveaux helpers pour raisons / texte de taux / toggle indexé
  getAltReasons(r: any): string[] {
    // On s'aligne sur le parent si fourni ; sinon pas de blocage ici.
    return this.validateAlternative?.(r)?.reasons ?? [];
  }

  getRateText(r: any): string {
    const sellSym = this.getTokenLabel(this.sellTokenSymbol);
    const buySym = this.getTokenLabel(this.buyTokenSymbol);
    const sd = Number(this.quote?.sellTokenDecimals ?? this.quote?.fromToken?.decimals ?? 18);
    const bd = Number(r?.toTokenDecimals ?? r?.buyTokenDecimals ?? r?.toToken?.decimals ?? this.quote?.buyTokenDecimals ?? this.quote?.toToken?.decimals ?? 18);
    const toNum = (v: any): number => {
      if (v == null || v === '') return NaN;
      if (typeof v === 'bigint') return Number(v);
      const s = String(v);
      if (s.startsWith('0x')) { try { return Number(BigInt(s)); } catch { return NaN; } }
      const n = Number(s);
      return isFinite(n) ? n : NaN;
    };
    let sell = toNum(this.quote?.formattedSellAmount);
    if (!isFinite(sell)) {
      const rawSell = toNum(this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.FromAmount);
      if (isFinite(rawSell)) sell = rawSell / Math.pow(10, sd);
    }
    let buy = toNum(r?.formattedBuyAmount);
    if (!isFinite(buy)) {
      const rawBuy = toNum(r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? r?.ToAmount);
      if (isFinite(rawBuy)) buy = rawBuy / Math.pow(10, bd);
    }
    if (!isFinite(sell) || !isFinite(buy) || sell <= 0 || buy <= 0) return '—';
    const rate = buy / sell;
    const rateStr = rate >= 1000 ? rate.toFixed(0) : rate.toFixed(6);
    return `1 ${sellSym} = ${rateStr} ${buySym}`;
  }

  toggleAlt(i: number) {
    // Bascule exclusive : un seul détail ouvert à la fois
    if (this.expandedAltIndex === i) {
      // ─── Fermer la carte i ────────────────────────────────────────────────
      this.expandedAltIndex = null;      // refermer → ré-afficher toutes les cartes
      this.expandedAlt[i] = false;

      // Reset des statuts/messages liés aux alternatives
      if (this.lastAltStatusIndex === i) this.lastAltStatusIndex = null;
      this.swapStatus = 'idle';
      this.swapError = null;
      this.swapHash = null;

    } else {
      // ─── Ouvrir la carte i (fermer l’ancienne si besoin) ─────────────────
      if (this.expandedAltIndex != null) {
        this.expandedAlt[this.expandedAltIndex] = false;
      }
      this.expandedAltIndex = i;
      this.expandedAlt[i] = true;

      // On repart propre sur une nouvelle carte
      this.lastAltStatusIndex = null;
      this.swapStatus = 'idle';
      this.swapError = null;
      this.swapHash = null;

      // Scroll doux juste sous le header
      setTimeout(() => {
        const host = this.scrollArea?.nativeElement;
        const cards = host?.querySelectorAll('[data-alt-card]') as NodeListOf<HTMLElement>;
        const el = cards && cards[i];
        if (el && host) {
          const top = el.offsetTop - 8; // petit offset
          host.scrollTo({ top, behavior: 'smooth' });
        }
      }, 0);
    }

    // Recalcul sticky footer après (dé)pliage
    setTimeout(() => this.recomputeStickyFooter(), 0);
  }


  // ⬇️ À coller DANS la classe QuotePreviewComponentV4

  /** Chaîne à utiliser pour l’explorer (main/alt). */
  pickChainIdForTx(x: any): number {
    // ordre: toChainId → chainId → quote.toChainId → quote.chainId → estimate/action → fallback fromChainId → 1
    const n = (v: any) => (v == null ? NaN : Number(v));
    const id =
      n(x?.toChainId) ||
      n(x?.chainId) ||
      n(this.quote?.toChainId) ||
      n(this.quote?.chainId) ||
      n(this.quote?.estimate?.toChainId) ||
      n(this.quote?.action?.toChainId) ||
      n(this.quote?.fromChainId) ||
      n(this.quote?.estimate?.fromChainId) ||
      n(this.quote?.action?.fromChainId) ||
      1;
    return isFinite(id) && id > 0 ? id : 1;
  }

  /** URL d’explorer pour un hash de tx. */
  getExplorerTxUrl(chainId: number | string | null, txHash: string | null): string {
    if (!chainId || !txHash) return '';
    const id = Number(chainId);

    // bases courantes (ajoute/édite selon tes réseaux supportés)
    const map: Record<number, string> = {
      1: 'https://etherscan.io/tx/',
      10: 'https://optimistic.etherscan.io/tx/',
      56: 'https://bscscan.com/tx/',
      137: 'https://polygonscan.com/tx/',
      42161: 'https://arbiscan.io/tx/',
      8453: 'https://basescan.org/tx/'
    };

    const base = map[id] || 'https://blockscout.com/tx/';
    return base + txHash;
  }

  /** Jamais de message brut MM ; mappe l’annulation et renvoie un message clean. */
  getFormattedBridgeError(error: any): string | null {
    if (!error) return null;

    // Annulation (EIP-1193 / Ethers v5/v6)
    const code = error?.code ?? error?.error?.code ?? error?.cause?.code;
    const msg = String(
      (typeof error === 'string' && error) ||
      error?.reason || error?.shortMessage || error?.error?.message ||
      error?.info?.error?.message || error?.data?.message || error?.message || ''
    ).toLowerCase();

    const isCancel =
      code === 4001 || code === 'ACTION_REJECTED' ||
      msg.includes('action rejected') || msg.includes('user denied') ||
      msg.includes('user rejected') || msg.includes('rejected by user') ||
      msg.includes('request rejected') || msg.includes('cancelled') || msg.includes('canceled');

    if (isCancel) return null; // le bloc "cancelled" gère l’affichage

    // Pas de dump hex/calldata/JSON verbeux
    const looksHex = /^0x[0-9a-fA-F]{24,}$/.test(msg.replace(/\s+/g, ''));
    const looksJson = msg.includes('{') || msg.includes('}') || msg.length > 160;
    if (looksHex || looksJson) return this.translate.instant('STATUS.ERROR');

    // Mappings utiles → sinon fallback générique
    if (msg.includes('insufficient funds')) return this.translate.instant('STATUS.INSUFFICIENT_FUNDS');
    if (msg.includes('intrinsic gas too low') || msg.includes('gas required exceeds')) return this.translate.instant('STATUS.GAS_TOO_LOW');
    if (msg.includes('execution reverted')) return this.translate.instant('STATUS.TX_REVERTED');
    if (msg.includes('timeout') || msg.includes('underpriced')) return this.translate.instant('STATUS.TX_TIMEOUT');
    if (msg.includes('nonce too low')) return this.translate.instant('STATUS.NONCE_TOO_LOW');
    if (msg.includes('already known') || msg.includes('already imported')) return this.translate.instant('STATUS.TX_ALREADY_IMPORTED');

    return this.translate.instant('STATUS.ERROR'); // “Erreur durant le swap.”
  }
  private pickFirstReason(reasons?: string[] | null): string | null {
    return Array.isArray(reasons) && reasons.length > 0 ? reasons[0] : null;
  }

  /** Libellé unique (1ʳᵉ raison) pour le WHY main, avec éventuel déficit fiat. */
  /** Libellé unique (1ʳᵉ raison) pour le WHY main, avec éventuel déficit fiat. */
  getMainFirstReasonLabel(): string | null {
    // ✅ lire d’abord les raisons renvoyées par le CTA (nouvelle source de vérité)
    const cta = this.getMainCtaState();
    const reasons = (cta?.reasons && cta.reasons.length > 0)
      ? cta.reasons
      : (this.bridgeReasons ?? []); // fallback rétro-compat

    const first = this.pickFirstReason(reasons);
    if (!first) return null;

    let label = this.getReasonLabel(first);

    // si gas insuffisant, afficher le déficit prioritairement depuis cta.deficitFiat
    if (first === 'INSUFFICIENT_NATIVE_GAS') {
      const deficit = (cta as any)?.deficitFiat ?? this.getMainGasDeficitFiat?.();
      if (deficit) {
        label += ` (${this.translate.instant('LABEL.NEED')} ${deficit})`;
      }
    }
    return label;
  }


  /** Libellé unique (1ʳᵉ raison) pour le WHY d’une alternative r, avec éventuel déficit fiat. */
  getAltFirstReasonLabel(r: any): string | null {
    const reasons = this.getAltReasons?.(r) ?? [];
    const first = this.pickFirstReason(reasons);
    if (!first) return null;
    let label = this.getReasonLabel(first, r);
    if (first === 'INSUFFICIENT_NATIVE_GAS') {
      const deficit = this.getAltGasDeficitFiat?.(r);
      if (deficit) label += ` (${this.translate.instant('LABEL.NEED')} ${deficit})`;
    }
    return label;
  }

  // Compat gas

  ngOnChanges(changes: SimpleChanges) {
    this.recomputeStickyFooter();
    if (changes['quote']) {
      const newKey = this.getProviderKey(this.quote) || null;
      this.currentQuoteKey = newKey;
      if (!this.selectedAltKey) this.selectedAltKey = newKey;
      this.applyGasFromQuote(this.quote);
    }


    clearTimeout(this.pendingTimeout);
    clearTimeout(this.autoHideTimeout);
    if (this.swapStatus === 'pending') {
      this.pendingTimeout = setTimeout(() => {
        if (this.swapStatus === 'pending') this.swapStatus = 'idle';
      }, 30000);
    } else if (this.swapStatus && this.swapStatus !== 'idle') {
      this.autoHideTimeout = setTimeout(() => {
        if (this.swapStatus && this.swapStatus !== 'pending') this.swapStatus = 'idle';
      }, 5000);
    }
    this.scheduleAutoWhy();
    this.kickWhyTimersOnce();
  }

  @HostListener('window:resize')
  onResize() { this.recomputeStickyFooter(); }

  private recomputeStickyFooter(): void {
    const el = this.scrollArea?.nativeElement;
    if (!el) { this.isScrollable = this.showStickyFooter = false; return; }
    // petite tolérance iOS pour éviter les off-by-one
    this.isScrollable = (el.scrollHeight > el.clientHeight + 2);
    const isExpanded = !!this.showMainDetails || !!this.showAlternatives || this.expandedAltIndex !== null;
    this.showStickyFooter = this.isScrollable && isExpanded;
  }

  private canonicalNativeSymbol(chainId?: number, raw?: string): string {
    const map: Record<number, string> = { 1: 'ETH', 10: 'ETH', 56: 'BNB', 137: 'MATIC', 42161: 'ETH', 8453: 'ETH' };
    if (chainId && map[chainId]) return map[chainId];
    const up = (raw || '').toUpperCase();
    if (up === 'POL' || up === 'MATIC') return map[137];
    return up || 'ETH';
  }

  get networkIconUrl(): string | null {
    const id = this.quote?.fromChainId ?? this.quote?.chainId ?? this.quote?.action?.fromChainId ?? this.quote?.estimate?.fromChainId ?? null;
    return getNetworkIcon(id);
  }
  isDarkMode(): boolean {
    return document.body.classList.contains('dark-mode');
  }
  get networkName(): string {
    const id = this.quote?.fromChainId ?? this.quote?.chainId ?? this.quote?.action?.fromChainId ?? this.quote?.estimate?.fromChainId ?? null;
    return getNetworkName(id);
  }

  get sellTokenSymbol(): string { return this.quote?.sellTokenSymbol ?? ''; }
  get buyTokenSymbol(): string { return this.quote?.buyTokenSymbol ?? ''; }

  // Nouveaux getters pour V2 : Focus sur received amount, provider, et rate (inspiré Uniswap)
  get providerName(): string {
    return this.quote?.providerName ?? this.quote?.ProviderName ?? this.getProviderKey(this.quote) ?? 'Uniswap API'; // Placeholder pro
  }

  get providerLogo(): string | null {
    return this.quote?.providerLogo ?? this.quote?.ToolLogoURI ?? this.quote?.toolLogoURI ?? this.quote?.logoURI ?? this.quote?.logo ?? this.quote?.Icon ?? this.quote?.icon ?? this.quote?.toolDetails?.logoURI ?? null;
  }

  get estimatedReceived(): string {
    const sym = this.getTokenLabel(this.buyTokenSymbol);
    const decimals =
      Number(this.quote?.buyTokenDecimals ?? this.quote?.BuyTokenDecimals ?? this.quote?.toToken?.decimals ?? 18);

    // 1) Si on a une valeur déjà formatée, on l’utilise telle quelle
    const formatted = this.quote?.formattedBuyAmount;
    let value: number;

    if (formatted !== undefined && formatted !== null && String(formatted) !== '') {
      value = Number(formatted);
    } else {
      // 2) Sinon on normalise le "raw" (wei-like)
      const raw = this.quote?.toAmount ?? this.quote?.ToAmount ?? this.quote?.toAmountMin ?? 0;
      const rawNum = typeof raw === 'bigint' ? Number(raw) : Number(raw || 0);
      value = rawNum / Math.pow(10, decimals);
    }

    if (!isFinite(value)) value = 0;
    return `${value.toFixed(6)} ${sym}`;
  }

  private formatFiat(usd: number): string {
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const fiat = usd * rate;
    const locale = this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US';
    if (fiat > 0 && fiat < 0.01) {
      return '< ' + (0.01).toLocaleString(locale, { style: 'currency', currency: this.fiatCode });
    }
    const underOne = fiat > 0 && fiat < 1;
    return fiat.toLocaleString(locale, {
      style: 'currency',
      currency: this.fiatCode,
      minimumFractionDigits: underOne ? 4 : 2,
      maximumFractionDigits: underOne ? 6 : 2,
    });
  }
  isAltExpanded(r: any): boolean {
    return this.expandedAlts.has(this.getProviderKey(r));
  }
  get mainFee() {
    const sym = this.canonicalNativeSymbol(Number(this.quote?.fromChainId));

    // 1) Préférence: champs LiFi déjà normalisés (V2)
    const lifiNative = Number((this.quote as any)?.lifiNetworkFeeNative ?? 0);
    const lifiUsd = Number((this.quote as any)?.lifiNetworkFeeUsd ?? 0);
    if (lifiNative > 0) {
      return {
        textEth: `${lifiNative.toFixed(6)} ${sym}`,
        textFiat: lifiUsd > 0 ? `≈ ${this.formatFiat(lifiUsd)}` : undefined,
      };
    }

    // 2) Fallback: totalWei (fullGasWei || _fallbackFullGasWei || 0)
    const totalWei =
      (this.fullGasWei && this.fullGasWei > 0n) ? this.fullGasWei :
        (this._fallbackFullGasWei && this._fallbackFullGasWei > 0n) ? this._fallbackFullGasWei : 0n;

    const native = Number(totalWei) / 1e18;
    const textEth = native ? `${native.toFixed(6)} ${sym}` : '';

    const usdPerNative = Number(
      this.quote?.gasTokenPriceUsd ??
      this.quote?.GasTokenPriceUsd ??
      this.nativeTokenUsd ??
      0
    );

    const usd = (usdPerNative && native) ? native * usdPerNative : 0;

    return {
      textEth,
      textFiat: usd > 0 ? `≈ ${this.formatFiat(usd)}` : undefined,
    };
  }

  // Remplace/ajoute ce getter
  get estimatedReceivedFiat(): string {
    // 1) Prix USD du token reçu
    const priceUsd = Number(this.quote?.buyTokenUsdPrice ?? this.quote?.BuyTokenUsdPrice ?? 0);

    // 2) Montant reçu (déjà formaté → sinon toAmount + décimales)
    let amount = Number(this.quote?.formattedBuyAmount ?? 0);
    if (!(amount > 0)) {
      const dec = Number(
        this.quote?.buyTokenDecimals ??
        this.quote?.BuyTokenDecimals ??
        this.quote?.toToken?.decimals ?? 18
      );

      const raw = this.quote?.toAmount ?? this.quote?.ToAmount ?? 0;
      let rawNum = 0;
      if (typeof raw === 'bigint') rawNum = Number(raw);
      else if (typeof raw === 'string' && raw.startsWith('0x')) {
        try { rawNum = Number(BigInt(raw)); } catch { rawNum = 0; }
      } else {
        rawNum = Number(raw || 0);
      }
      amount = rawNum / Math.pow(10, dec);
    }

    // 3) Valeur en USD → format fiat à 2 décimales fixes
    const usd = priceUsd * amount;
    if (!(usd > 0)) return '';

    const code = this.fiatCode ?? 'USD';
    const rate = this.fiatRates?.[code] ?? 1;
    const locale = (this as any).getLocaleForCurrency
      ? (this as any).getLocaleForCurrency(code)
      : (code === 'EUR' ? 'fr-FR' : 'en-US');

    const fiat = usd * rate;
    return `≈ ${fiat.toLocaleString(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }


  getRouteEstimatedBuyFiat(r: any): string {
    // 1) USD direct si présent (plusieurs variantes de casse)
    let usd =
      Number(r?.toAmountUsd ?? 0) ||
      Number(r?.ToAmountUsd ?? 0) ||
      Number((r as any)?.toAmountUSD ?? 0);

    // 2) Sinon on calcule: montant * prix USD
    if (!(usd > 0)) {
      // montant reçu (préférer le formaté)
      let amt = Number(r?.formattedBuyAmount ?? 0);
      if (!(amt > 0)) {
        const dec = Number(
          r?.toTokenDecimals ?? r?.buyTokenDecimals ??
          r?.toToken?.decimals ?? this.quote?.buyTokenDecimals ??
          this.quote?.BuyTokenDecimals ?? 18
        );

        const raw = r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? r?.ToAmount ?? 0;
        let rawNum = 0;
        if (typeof raw === 'bigint') rawNum = Number(raw);
        else if (typeof raw === 'string' && raw.startsWith('0x')) {
          try { rawNum = Number(BigInt(raw)); } catch { rawNum = 0; }
        } else { rawNum = Number(raw || 0); }
        amt = rawNum / Math.pow(10, dec);
      }

      const priceUsd = Number(
        r?.buyTokenUsdPrice ?? r?.BuyTokenUsdPrice ??
        this.quote?.buyTokenUsdPrice ?? this.quote?.BuyTokenUsdPrice ?? 0
      );

      if (amt > 0 && priceUsd > 0) usd = amt * priceUsd;
    }

    if (!(usd > 0)) return '';

    // 3) Format FIAT à **2 décimales fixes**
    const code = this.fiatCode ?? 'USD';
    const rate = this.fiatRates?.[code] ?? 1;
    const locale = (this as any).getLocaleForCurrency
      ? (this as any).getLocaleForCurrency(code)
      : (code === 'EUR' ? 'fr-FR' : 'en-US');

    const fiat = usd * rate;

    return `≈ ${fiat.toLocaleString(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }



  get exchangeRate(): string {
    const sellSym = this.getTokenLabel(this.sellTokenSymbol);
    const buySym = this.getTokenLabel(this.buyTokenSymbol);

    const sellDecimals = Number(this.quote?.sellTokenDecimals ?? this.quote?.fromToken?.decimals ?? 18);
    const buyDecimals = Number(this.quote?.buyTokenDecimals ?? this.quote?.toToken?.decimals ?? 18);

    // Utilitaires
    const toNum = (v: any): number => {
      if (v == null || v === '') return NaN;
      if (typeof v === 'bigint') return Number(v);
      const s = String(v);
      if (s.startsWith('0x')) { try { return Number(BigInt(s)); } catch { return NaN; } }
      const n = Number(s);
      return isFinite(n) ? n : NaN;
    };

    // 1) On privilégie les valeurs déjà formatées (unités humaines)
    let sell = toNum(this.quote?.formattedSellAmount);
    let buy = toNum(this.quote?.formattedBuyAmount);

    // 2) Sinon, fallback sur les montants "bruts" + normalisation par decimals
    if (!isFinite(sell)) {
      const rawSell = toNum(this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.FromAmount);
      if (isFinite(rawSell)) sell = rawSell / Math.pow(10, sellDecimals);
    }
    if (!isFinite(buy)) {
      const rawBuy = toNum(this.quote?.toAmount ?? this.quote?.buyAmount ?? this.quote?.ToAmount);
      if (isFinite(rawBuy)) buy = rawBuy / Math.pow(10, buyDecimals);
    }

    if (!isFinite(sell) || !isFinite(buy) || sell <= 0 || buy <= 0) return '—';

    // 3) 1 SELL = (buy / sell) BUY
    const rate = buy / sell;
    const rateStr = rate >= 1000 ? rate.toFixed(0) : rate.toFixed(6);

    return `1 ${sellSym} = ${rateStr} ${buySym}`;
  }


  // Détails dépliables (comme Uniswap : fees, network cost, slippage)
  get routeFee(): string {
    // Frais de route (e.g., 0.25% Uniswap)
    return this.quote?.routeFeePct ? `${this.quote.routeFeePct}%` : '0.3%'; // Placeholder
  }

  // Option 1: si tu as déjà une méthode formatFiat(usd:number): string (cf. patch précédent),
  // alors le getter peut être ultra simple :
  get networkCost(): string {
    const gasFeeUsd = Number(this.quote?.gasFeeUsd ?? this.quote?.GasFeeUsd ?? 0);
    if (!(gasFeeUsd > 0)) return '—';
    // formatFiat prend un montant en USD et applique le taux + locale + décimales adaptatives
    return this.formatFiat(gasFeeUsd); // ex: "€0.0018" ou "< €0.01"
  }


  get priceImpact(): string {
    // décimales
    const sd = Number(this.quote?.sellTokenDecimals ?? this.quote?.SellTokenDecimals ?? this.quote?.fromToken?.decimals ?? 18);
    const bd = Number(this.quote?.buyTokenDecimals ?? this.quote?.BuyTokenDecimals ?? this.quote?.toToken?.decimals ?? 18);

    // montants bruts → humains
    const toNum = (v: any) => {
      if (v == null || v === '') return NaN;
      if (typeof v === 'bigint') return Number(v);
      const s = String(v); if (s.startsWith('0x')) { try { return Number(BigInt(s)); } catch { return NaN; } }
      const n = Number(s); return isFinite(n) ? n : NaN;
    };

    let sell = toNum(this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.SellAmount);
    let buy = toNum(this.quote?.toAmount ?? this.quote?.buyAmount ?? this.quote?.ToAmount);

    if (!isFinite(sell) || !isFinite(buy)) return '—';
    sell = sell / Math.pow(10, sd);
    buy = buy / Math.pow(10, bd);

    // prix USD
    const sellUsd = Number(this.quote?.sellTokenUsdPrice ?? this.quote?.SellTokenUsdPrice ?? 0);
    const buyUsd = Number(this.quote?.buyTokenUsdPrice ?? this.quote?.BuyTokenUsdPrice ?? 0);

    // fallback si LiFi a directement donné from/toAmountUSD
    const fromUsd = Number((this.quote as any)?.fromAmountUsd ?? 0);
    const toUsd = Number((this.quote as any)?.toAmountUsd ?? 0);
    const buyFair = (sellUsd > 0 && buyUsd > 0)
      ? (sell * sellUsd) / buyUsd
      : (fromUsd > 0 && buy > 0) ? (fromUsd / buyUsd) : NaN;

    if (!isFinite(buyFair) || buyFair <= 0) return '—';

    let impact = 1 - (buy / buyFair);

    // optionnel: retirer l’aggregator fee si dispo (isoler l’impact "pur")
    const integFeeUsd = Number((this.quote as any)?.integratorFeeUsd ?? 0);
    if (integFeeUsd > 0 && sellUsd > 0) {
      const buyFairAdj = ((sell * sellUsd) - integFeeUsd) / buyUsd;
      if (buyFairAdj > 0) impact = 1 - (buy / buyFairAdj);
    }

    const pct = Math.max(0, impact) * 100;
    return `${pct.toFixed(pct >= 1 ? 2 : 3)}%`;
  }


  get maxSlippage(): string {
    // Effet de glissement max (comme image : 1.04%)
    return this.quote?.maxSlippagePct ? `${this.quote.maxSlippagePct}%` : '0.5%'; // Placeholder
  }

  // Gas fee (réutilisé)
  get displayedGasFee(): string {
    const nat = this.canonicalNativeSymbol(Number(this.quote?.fromChainId));
    const totalWei = this.fullGasWei > 0n ? this.fullGasWei : this._fallbackFullGasWei > 0n ? this._fallbackFullGasWei : 0n;
    const eth = Number(totalWei) / 1e18;
    return `${eth.toFixed(6)} ${nat}`;
  }

  getGasFeeFiat(): string | null {
    if (!this.nativeTokenUsd) return null;
    const totalWei = this.fullGasWei > 0n ? this.fullGasWei : this._fallbackFullGasWei > 0n ? this._fallbackFullGasWei : 0n;
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const eth = Number(totalWei) / 1e18;
    const usd = eth * this.nativeTokenUsd;
    const fiat = usd * rate;
    return (Math.round(fiat * 100) / 100).toLocaleString(this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US', { style: 'currency', currency: this.fiatCode });
  }

  getTokenLabel(symbol: string): string {
    const key = 'TOKENS.' + symbol.toUpperCase();
    const translated = this.translate.instant(key);
    return translated !== key ? translated : symbol;
  }

  // Alternatives (compact : received + provider, détails on click)
  get otherRoutes(): any[] {
    const all = Array.isArray(this.quote?.singleRoutes) ? this.quote.singleRoutes : [];
    const current = this.quote || {};
    return all.filter((r: any) => !this.isSameProvider(r, current));
  }

  private getRouteKey(r: any): string {
    const raw = r?.providerKey ?? r?.ProviderKey ?? r?.toolKey ?? r?.ToolKey ?? r?.tool ?? r?.Tool ?? r?.source ?? r?.Source ?? r?.providerName ?? r?.ProviderName ?? r?.name ?? '';
    return String(raw || '').toLowerCase().trim();
  }

  isAltSelected(r: any): boolean {
    const k = this.getProviderKey(r);
    return !!k && (k === this.selectedAltKey || k === this.currentQuoteKey);
  }

  private isSameProvider(a: any, b: any): boolean {
    return this.getProviderKey(a) === this.getProviderKey(b);
  }

  getRouteProvider(r: any): string {
    return (r?.providerName ?? r?.ProviderName) || this.getProviderKey(r);
  }

  getRouteLogo(r: any): string | null {
    return r?.providerLogo ?? r?.ToolLogoURI ?? r?.toolLogoURI ?? r?.logoURI ?? r?.logo ?? r?.Icon ?? r?.icon ?? r?.toolDetails?.logoURI ?? null;
  }

  getRouteEstimatedBuy(r: any): string {
    const raw = Number(r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? 0);
    const dec = Number(r?.toTokenDecimals ?? r?.toToken?.decimals ?? this.quote?.buyTokenDecimals ?? this.quote?.BuyTokenDecimals ?? 18);
    const val = raw / Math.pow(10, dec);
    return `${this.formatAmount(val, 6)} ${this.getRouteBuySymbol(r)}`;
  }

  getRouteBuySymbol(r: any): string {
    return r?.buyTokenSymbol ?? r?.toTokenSymbol ?? r?.action?.toToken?.symbol ?? this.quote?.buyTokenSymbol ?? this.quote?.BuyTokenSymbol ?? '—';
  }

  formatAmount(value: number, decimals: number): string {
    return value.toFixed(decimals);
  }

  isAlternativeDisabled(r: any): boolean {
    if (this.swapDisabled) return true;
    const tx = r?.transactionRequest ?? r?.TransactionRequest ?? null;
    const hasTx = !!(tx?.to && tx?.data) || !!(r?.to && r?.data) || !!(r?.target && r?.data);
    const hasTyped = !!(r?.domain && r?.types && r?.value);
    return !(hasTx || hasTyped) || (this.validateAlternative?.(r)?.disabled ?? false);
  }

  onValidateAlternative(route: any) {
    const key = this.getProviderKey(route);
    this.selectedAltKey = key;
    this.selectAlternative.emit(route);
    // Optionnel : toggle détails pour cette alternative
    this.showDetails = true;
  }

  // UI Toggles (inspiré Uniswap : clean, minimal interactions)
  toggleDetails() {
    this.showDetails = !this.showDetails;
  }

  toggleAlternatives(): void {
    this.showAlternatives = !this.showAlternatives;
    setTimeout(() => this.recomputeStickyFooter(), 0);
  }






  /** ───────────── Helpers pour alternatives ───────────── */


  private _toNum(v: any): number {
    if (v == null || v === '') return NaN;
    if (typeof v === 'bigint') return Number(v);
    const s = String(v);
    if (s.startsWith('0x')) { try { return Number(BigInt(s)); } catch { return NaN; } }
    const n = Number(s);
    return isFinite(n) ? n : NaN;
  }
  private _toWei(v: any): bigint {
    try {
      if (typeof v === 'bigint') return v;
      if (typeof v === 'number') return BigInt(Math.trunc(v));
      if (typeof v === 'string') return v ? BigInt(v) : 0n;
    } catch { }
    return 0n;
  }

  /** ───────────── ROUTE_FEE ─────────────
   *  Essayez d'avoir r.routeFeePct côté back; sinon fallback raisonnable.
   */
  getRouteFee(r: any): string {
    const pct = r?.routeFeePct ?? this.quote?.routeFeePct;
    return (pct != null) ? `${pct}%` : '0.3%';
  }

  /** ───────────── NETWORK_COST (fiat) ───────────── */
  getRouteNetworkCost(r: any): string {
    const usd = Number(r?.gasFeeUsd ?? r?.GasFeeUsd ?? 0);
    if (usd > 0) return this.formatFiat(usd);

    // Fallback: (wei -> natif) * priceUsd
    const wei = this._toWei(r?.gasAmountWei ?? r?.GasAmountWei ?? r?.totalNetworkFee ?? r?.TotalNetworkFee ?? 0);
    const native = Number(wei) / 1e18;
    const usdPerNative = Number(
      r?.gasTokenPriceUsd ?? r?.GasTokenPriceUsd ??
      this.quote?.gasTokenPriceUsd ?? this.quote?.GasTokenPriceUsd ??
      this.nativeTokenUsd ?? 0
    );
    const estUsd = native && usdPerNative ? native * usdPerNative : 0;
    return estUsd > 0 ? this.formatFiat(estUsd) : '—';
  }

  /** ───────────── NETWORK_FEE (natif + ≈ fiat) ───────────── */
  getRouteDisplayedGasFee(r: any): { textEth: string; textFiat?: string } {
    const chainId = Number(r?.fromChainId ?? this.quote?.fromChainId);
    const symbol = this.canonicalNativeSymbol(chainId, r?.GasTokenSymbol ?? r?.gasTokenSymbol);
    const decimals = Number(r?.GasTokenDecimals ?? r?.gasTokenDecimals ?? 18) || 18;
    const pow10 = Math.pow(10, decimals);

    // 1) montant en "wei"
    let feeWei =
      this._toWei(r?.lifiGasAmountWei ?? r?.LifiGasAmountWei) ||
      (() => {
        const p = this._toNum(r?.lifiGasPriceWei ?? r?.LifiGasPriceWei);
        const g = this._toNum(r?.lifiEstimatedGas ?? r?.LifiEstimatedGas);
        return (p && g) ? BigInt(Math.round(p * g)) : 0n;
      })() ||
      this._toWei(r?.gasAmountWei ?? r?.GasAmountWei ?? r?.totalNetworkFee ?? r?.TotalNetworkFee);

    const native = Number(feeWei) / pow10;

    // 2) affichage natif (avec seuil)
    const NATIVE_MIN = 1e-8;
    let textEth = '';
    if (native > 0) {
      textEth = native >= NATIVE_MIN
        ? `${native.toFixed(8)} ${symbol}`
        : `<${NATIVE_MIN.toFixed(8)} ${symbol}`;
    }

    // 3) USD/Fiat
    let usd = this._toNum(r?.lifiGasFeeUsd ?? r?.LifiGasFeeUsd ?? r?.gasFeeUsd ?? r?.GasFeeUsd);
    if (!usd && native > 0) {
      const usdPerNative =
        this._toNum(r?.lifiGasTokenPriceUsd ?? r?.LifiGasTokenPriceUsd) ||
        this._toNum(r?.gasTokenPriceUsd ?? r?.GasTokenPriceUsd) ||
        (this.nativeTokenUsd || 0);
      if (usdPerNative) usd = native * usdPerNative;
    }

    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const locale = this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US';
    const FIAT_MIN = 0.01;
    let textFiat = '';
    const fiat = usd * rate;
    if (fiat > 0) {
      textFiat = fiat >= FIAT_MIN
        ? `≈ ${fiat.toLocaleString(locale, { style: 'currency', currency: this.fiatCode })}`
        : `≈ < ${FIAT_MIN.toLocaleString(locale, { style: 'currency', currency: this.fiatCode })}`;
    }

    return { textEth, textFiat };
  }


  /** ───────────── PRICE_IMPACT (%) ───────────── */
  getRoutePriceImpact(r: any): string {
    // décimales (sell sur la quote, buy sur la route)
    const sd = Number(
      r?.sellTokenDecimals ?? this.quote?.sellTokenDecimals ?? this.quote?.SellTokenDecimals ??
      r?.fromToken?.decimals ?? this.quote?.fromToken?.decimals ?? 18
    );
    const bd = Number(
      r?.toTokenDecimals ?? r?.buyTokenDecimals ?? this.quote?.buyTokenDecimals ?? this.quote?.BuyTokenDecimals ??
      r?.toToken?.decimals ?? this.quote?.toToken?.decimals ?? 18
    );

    // montants normalisés
    let sell = this._toNum(this.quote?.formattedSellAmount);
    if (!isFinite(sell)) {
      const rawSell = this._toNum(this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.FromAmount);
      if (isFinite(rawSell)) sell = rawSell / Math.pow(10, sd);
    }
    let buy = this._toNum(r?.formattedBuyAmount);
    if (!isFinite(buy)) {
      const rawBuy = this._toNum(r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? r?.ToAmount);
      if (isFinite(rawBuy)) buy = rawBuy / Math.pow(10, bd);
    }
    if (!isFinite(sell) || !isFinite(buy) || sell <= 0 || buy <= 0) return '—';

    // prix USD (pivot)
    const sellUsd = Number(this.quote?.sellTokenUsdPrice ?? this.quote?.SellTokenUsdPrice ?? 0);
    const buyUsd = Number(this.quote?.buyTokenUsdPrice ?? this.quote?.BuyTokenUsdPrice ?? 0);
    const fromUsd = Number(this.quote?.fromAmountUsd ?? r?.fromAmountUsd ?? 0); // fallback éventuel

    let buyFair = (sellUsd > 0 && buyUsd > 0)
      ? (sell * sellUsd) / buyUsd
      : (fromUsd > 0 && buyUsd > 0) ? (fromUsd / buyUsd) : NaN;

    if (!isFinite(buyFair) || buyFair <= 0) return '—';

    // option : retirer l’intégrator fee USD si dispo (impact “pur”)
    const integFeeUsd = Number(r?.integratorFeeUsd ?? this.quote?.integratorFeeUsd ?? 0);
    if (integFeeUsd > 0 && sellUsd > 0) {
      const adj = ((sell * sellUsd) - integFeeUsd) / (buyUsd || 1);
      if (adj > 0 && isFinite(adj)) buyFair = adj;
    }

    const impact = Math.max(0, 1 - (buy / buyFair));
    const pct = impact * 100;
    return `${pct.toFixed(pct >= 1 ? 2 : 3)}%`;
  }

  /** ───────────── MAX_SLIPPAGE (%) ───────────── */
  getRouteMaxSlippage(r: any): string {
    const v = r?.maxSlippagePct ?? this.quote?.maxSlippagePct;
    return (v != null) ? `${v}%` : '0.5%';
  }











  // Compatibilité : applyGasFromQuote, etc.
  applyGasFromQuote(q: any) {
    const expectedWei = this.parseWei(q?.mmExpectedNetworkFeeWei ?? q?.mmNetworkFeeWei ?? 0);
    const totalWei = expectedWei > 0n
      ? expectedWei
      : this.parseWei(q?.totalNetworkFee ?? q?.TotalNetworkFee ?? 0);
    this._fallbackFullGasWei = totalWei
    this.nativeTokenUsd = Number(q?.gasTokenPriceUsd ?? q?.GasTokenPriceUsd ?? 0);
    if ((!this.nativeTokenUsd || this.nativeTokenUsd === 0) && totalWei > 0n) {
      const feeUsd = Number(q?.gasFeeUsd ?? q?.GasFeeUsd ?? 0);
      if (feeUsd) {
        const feeEth = Number(totalWei) / 1e18;
        this.nativeTokenUsd = feeEth ? (feeUsd / feeEth) : 0;
      }
    }
  }

  onShareClick(): void {
    const baseUrl = window.location.origin + '/';

    const tokenIn = this.sellTokenSymbol;
    const tokenOut = this.buyTokenSymbol;
    const amountIn = String(this.quote?.formattedSellAmount ?? '');

    // robust extraction of fromChainId
    const fromChainId =
      this.quote?.fromChainId ??
      this.quote?.chainId ??
      this.quote?.action?.fromChainId ??
      this.quote?.estimate?.fromChainId ?? '';

    const shareUrl = `${baseUrl}?tokenIn=${encodeURIComponent(tokenIn)}&tokenOut=${encodeURIComponent(tokenOut)}&amountIn=${encodeURIComponent(amountIn)}&fromChainId=${encodeURIComponent(String(fromChainId))}`;

    if (navigator.share) {
      navigator.share({
        title: 'SmartSwap',
        text: this.translate.instant('DEFI_OPPORTUNITY'),
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => {
        // Toast feedback (pro, comme Uniswap)
        setTimeout(() => { }, 3000);
      });
    }
  }


  closePreview() { this.close.emit(); }

  getFormattedSwapError(err: any): string {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return JSON.stringify(err);
  }

  isUserCancelled(err: string | null): boolean {
    return err?.includes('cancelled') ?? false; // Placeholder
  }


  toggleAltDetails(route: any) {
    const key = this.getProviderKey(route);
    if (this.expandedAlts.has(key)) {
      this.expandedAlts.delete(key);
    } else {
      this.expandedAlts.add(key);
    }
  }
  ngOnDestroy() {
    clearTimeout(this.pendingTimeout);
    clearTimeout(this.autoHideTimeout);
    clearTimeout(this.tooltipTimeout);
  }
}
